import React from "react";
import { Link } from "react-router-dom";
import { ScrollText, ArrowLeft, Mail } from "lucide-react";
import SakuraDivider from "@/components/SakuraDivider";

export default function Terms() {
  return (
    <div data-testid="page-terms">
      <section className="section">
        <div className="mx-auto max-w-4xl">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-sakura-500 hover:underline mb-5"><ArrowLeft className="h-4 w-4" /> Back home</Link>
          <div className="flex items-center gap-3">
            <span className="h-12 w-12 grid place-items-center rounded-full bg-sakura-200 text-sakura-500"><ScrollText className="h-5 w-5" /></span>
            <h1 className="font-serif text-4xl sm:text-5xl text-sakura-ink">Terms of Service</h1>
          </div>
          <p className="mt-3 text-sm text-sakura-soft">Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
          <SakuraDivider className="my-8" />

          <div className="max-w-none space-y-6 text-sakura-ink">
            <p>
              These Terms of Service ("Terms") govern your use of the Sakura Treats website
              and the purchase of any custom treats from us. By submitting an inquiry or
              paying a deposit, you agree to these Terms.
            </p>

            <h2 className="font-serif text-2xl text-sakura-ink">1. Florida Cottage Food Disclosure</h2>
            <p className="text-sakura-soft">
              These products are homemade and produced in a cottage food operation that is
              not subject to Florida's food safety regulations.
            </p>

            <h2 className="font-serif text-2xl text-sakura-ink">2. Orders & Quotes</h2>
            <ul className="list-disc pl-5 space-y-2 text-sakura-soft">
              <li>Submitting an inquiry is a request for a quote, not a confirmed order.</li>
              <li>An order is confirmed only after (a) we reply with a final quote, (b) you accept the details in writing, and (c) the deposit is paid.</li>
              <li>We recommend submitting inquiries at least 1–2 weeks in advance. Rush orders may be accepted depending on availability.</li>
            </ul>

            <h2 className="font-serif text-2xl text-sakura-ink">3. Deposits, Payments & Refunds</h2>
            <ul className="list-disc pl-5 space-y-2 text-sakura-soft">
              <li><b>Orders under $30:</b> full payment is required at the time of booking.</li>
              <li><b>Orders over $30:</b> a 50% non-refundable deposit is required to secure your event date, with the remaining balance due at pickup.</li>
              <li>Payments are processed by Stripe. We do not store your card details.</li>
              <li>Deposits may be applied toward a rescheduled event within 60 days, subject to availability, but are otherwise non-refundable.</li>
            </ul>

            <h2 className="font-serif text-2xl text-sakura-ink">4. Cancellations & Changes</h2>
            <ul className="list-disc pl-5 space-y-2 text-sakura-soft">
              <li><b>7+ days before event:</b> changes to flavors, colors, and quantities are generally possible, subject to confirmation.</li>
              <li><b>Under 7 days before event:</b> changes may not be possible. Cancellations forfeit the deposit.</li>
              <li>We reserve the right to decline or cancel any order; in that case any deposit will be refunded in full.</li>
            </ul>

            <h2 className="font-serif text-2xl text-sakura-ink">5. Pickup & Delivery</h2>
            <ul className="list-disc pl-5 space-y-2 text-sakura-soft">
              <li>Local pickup in Spring Hill, FL is preferred.</li>
              <li>Delivery may be available within the Tampa Bay / Pasco County area for an additional fee, subject to availability.</li>
              <li>Once treats leave our hands, you are responsible for their handling, storage, and transport.</li>
            </ul>

            <h2 className="font-serif text-2xl text-sakura-ink">6. Shipping</h2>
            <p className="text-sakura-soft">
              Shipping may be limited depending on treat type, weather, and packaging.
              When shipping is offered, risk of loss passes to you upon hand-off to the
              carrier. We are not responsible for melting, damage, or delays by the carrier.
            </p>

            <h2 className="font-serif text-2xl text-sakura-ink">7. Allergens</h2>
            <p className="text-sakura-soft">
              Our kitchen handles wheat, dairy, eggs, soy, tree nuts, and peanuts. Even items
              listed as "nut-free" may be cross-contaminated. Please disclose allergies in
              your inquiry — we cannot guarantee an allergen-free product.
            </p>

            <h2 className="font-serif text-2xl text-sakura-ink">8. Photos & Marketing</h2>
            <p className="text-sakura-soft">
              We may photograph your order for use in our portfolio, website gallery, and
              social media. No personal information (e.g., names, addresses, event photos of
              people) will be shared. If you'd prefer we not post, please tell us at the
              time of your order.
            </p>

            <h2 className="font-serif text-2xl text-sakura-ink">9. Intellectual Property</h2>
            <p className="text-sakura-soft">
              You confirm that any inspiration images you send us are either yours or used
              with permission, and that you hold the rights to any brand/character you
              request we reproduce.
            </p>

            <h2 className="font-serif text-2xl text-sakura-ink">10. Limitation of Liability</h2>
            <p className="text-sakura-soft">
              To the fullest extent permitted by Florida law, Sakura Treats' total liability
              for any order is limited to the amount you paid for that order. We are not
              liable for indirect, incidental, or consequential damages.
            </p>

            <h2 className="font-serif text-2xl text-sakura-ink">11. Governing Law</h2>
            <p className="text-sakura-soft">
              These Terms are governed by the laws of the State of Florida. Any dispute
              will be resolved in the courts of Pasco County, Florida.
            </p>

            <h2 className="font-serif text-2xl text-sakura-ink">12. Contact</h2>
            <p className="text-sakura-soft">
              Questions? <Mail className="inline h-4 w-4 mx-1 text-sakura-500" />
              <a href="mailto:sakuratreats.shop@gmail.com" className="text-sakura-500 underline">sakuratreats.shop@gmail.com</a>.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
